import { useCallback, useState } from 'react';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import { showErrorMessage } from '../../../../util/errorPopupUtil';
import { ErrorStrings } from '../../../../constants/fixedMessages';
import { setLoading as setLoadingAction, addNoteToSelectedTicket } from '../../../../actions';
import { GORGIAS, ZENDESK, ZENDESK_CHAT } from '../../../../constants/conversationTypes';
import { setZendeskChatTicketTags } from '../../../../api/tickets/zendesk';
import { updateDummyButtonZendeskTags } from '../../../../actions/tools/ZendeskActions';
import { updateGorgiasDummyTicketTags } from '../../../../actions/tools/GorgiasActions';
import { createLastMileRecord } from '../../../../actions/tools/LastMileActions';
import { oneBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import { useDispatch, useSelector } from 'react-redux';
import {
  extractSelectedTicketFromState,
  extractSelectedWizardMacro,
  getAllMessages,
  getDecisionNodeAtPath,
} from '../../../../extractFromState';
import { handleError } from 'services/ErrorService/ErrorService';
import * as trackAction from '../../../../analytics/buttonEventAnalytics';

export function useDummyButton(initialProps) {
  const dispatch = useDispatch();
  const { dummyButtonActionTag, showLoading = true, shouldCreateLastMileRecord = true } = initialProps;
  const [dummyButtonHasBeenClicked, setDummyButtonHasBeenClicked] = useState(false);
  const setLoading = useCallback(
    (loading) => (showLoading ? dispatch(setLoadingAction(loading, 'CopyTextContainer')) : null),
    [dispatch, showLoading]
  );
  const selectedTicket = useSelector(extractSelectedTicketFromState);
  const selectedTicketMessages = useSelector(getAllMessages);
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const selectedMacro = useSelector(extractSelectedWizardMacro);

  const dummyTags = isString(dummyButtonActionTag) ? [dummyButtonActionTag] : dummyButtonActionTag?.tags ?? [];

  const handleDummyButtonClick = async () => {
    if (!selectedTicket) {
      showErrorMessage(new Error(ErrorStrings.SELECTED_TICKET_NOT_FOUND));

      return;
    }
    if (decisionNode) {
      trackAction.trackDummyButtonClickNode(decisionNode);
    } else if (selectedMacro) {
      trackAction.trackDummyButtonClickMacro(selectedMacro);
    }

    const mappedTags = dummyTags.map((tag) => ({ id: tag, value: tag }));

    try {
      setLoading(true);
      switch (selectedTicket.ticketType) {
        case ZENDESK_CHAT:
          await setZendeskChatTicketTags(
            selectedTicket.simplrAccountPtrId,
            selectedTicket.platformTicketId,
            selectedTicket.ticketId,
            dummyTags
          );
          break;
        case ZENDESK:
          await updateDummyButtonZendeskTags(selectedTicket, dummyTags);
          break;
        case GORGIAS:
          await dispatch(updateGorgiasDummyTicketTags(mappedTags));
          break;
        default:
          await dispatch(
            addNoteToSelectedTicket(
              `The ${dummyTags.join(', ')} ${
                dummyTags.length > 1 ? 'actions have' : 'action has'
              } been successfully executed.`
            )
          );
          break;
      }

      // used as a backup incase the note is not populated in UI due to livequery bug
      setDummyButtonHasBeenClicked(true);
      if (shouldCreateLastMileRecord) {
        const dummyBtnInfo = {
          buttonLabel: dummyButtonActionTag?.label ?? dummyButtonActionTag,
          dummyButtonActionTags: dummyTags,
        };
        dispatch(createLastMileRecord(dummyBtnInfo));
      }

      oneBtnPopupCreator({
        content: {
          main: `Action executed successfully!`,
          iconPath: '/images/checkmark-green.svg',
        },
        btnConfig: 'CLOSE',
      });
    } catch (error) {
      handleError({ error, customMessage: 'Something went wrong while performing dummy action.', showPopup: true });
    } finally {
      setLoading(false);
    }
  };

  const isDummyActionUsed = () => {
    if (!dummyButtonActionTag || isEmpty(dummyTags)) {
      return true;
    }

    const note = `The ${dummyTags.join(', ')} ${
      dummyTags.length > 1 ? 'actions have' : 'action has'
    } been successfully executed.`;

    const result = selectedTicketMessages.find((message) => {
      return message.noteType === 'HINT' && message.messageBody === note;
    });

    // notes occasionally are not populated in the UI due to bug
    // dummyButtonHasBeenClicked protects against livequery bug

    return dummyButtonHasBeenClicked || Boolean(result);
  };

  return {
    handleDummyButtonClick,
    isDummyActionUsed,
  };
}
